import { useEffect, useState } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { DateField } from '../../../../components/inputs/DateField';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { Textarea } from '../../../../components/inputs/Textarea';
import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { focusValidationError } from '../../../../lib/validationFocus';
import { personnelApi, personsApi } from '../../../human-resources/personnel/personnelApi';
import { maintenanceItemsApi } from '../buildingManagementApi';
import {
  ACTIVITY_TYPE_LABEL,
  type MaintenanceActivityType,
  type MaintenanceItemDetail,
} from '../buildingManagementTypes';
import { sampleMaintenanceRecord } from '../utils/sampleBuildingManagement';
import { MAINTENANCE_DOCUMENT_MAX_BYTES, MaintenanceDocumentInput } from './MaintenanceDocumentInput';

/** The server's `BuildingManagementFieldRules.ActivityMaxLength`. */
const ACTIVITY_MAX_LENGTH = 200;

/**
 * Record an ACTIVITY performed against an item — maintenance, compliance or a submission — by
 * whom, when, with remarks and a document. Product call, 12 Oct 2026: not every activity on a
 * building obligation is maintenance.
 *
 * **Renewing the schedule is OPT-IN.** Ticking "This renews the schedule" reveals the next due
 * date, and the item's due date moves to it on Submit; unticked, the activity is logged and the
 * schedule is left alone. The date is SUGGESTED one period after the current due date (the
 * server's arithmetic) — a renewed permit carries its own expiry, which is the reader's to type.
 *
 * **Two calls, in order: the activity, then the document.** The document needs the activity's
 * id. If the activity lands and the document does not, the form has nothing left to report into
 * — the activity is real — so it closes and a toast says to file it from the Activities tab.
 */
export function RecordMaintenanceModal({
  open,
  item,
  onClose,
  onSaved,
}: {
  open: boolean;
  item: MaintenanceItemDetail;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const canReadPeople = usePermission('personnel.read');

  const [type, setType] = useState('');
  const [activity, setActivity] = useState('');
  const [performedOn, setPerformedOn] = useState(todayCalendarDate());
  const [performedBy, setPerformedBy] = useState('');
  const [renews, setRenews] = useState(false);
  const [nextDueDate, setNextDueDate] = useState(item.suggestedNextDueDate);
  const [remarks, setRemarks] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const [staff, setStaff] = useState<{ id: string; name: string }[]>([]);
  const [loadingStaff, setLoadingStaff] = useState(canReadPeople);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [percent, setPercent] = useState(0);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!open || !canReadPeople) return;

    const controller = new AbortController();

    personnelApi
      .list({ pageSize: 100 }, controller.signal)
      .then(async (personnel) => {
        const names = await personsApi
          .resolve(
            personnel.items.map((p) => p.personId).filter((id): id is string => id != null),
            controller.signal,
          )
          .catch(() => []);

        if (controller.signal.aborted) return;

        const byPersonId = new Map(names.map((n) => [n.personId, n.displayName]));

        setStaff(
          personnel.items.map((p) => ({
            id: p.id,
            name: (p.personId != null ? byPersonId.get(p.personId) : undefined) ?? p.employeeNumber,
          })),
        );
      })
      // A failed picker load names no field — it must not reach the save path's error state.
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setLoadingStaff(false);
      });

    return () => controller.abort();
  }, [open, canReadPeople]);

  // The same rule the server enforces, stated before the request rather than after it.
  const orderValid = !renews || performedOn === '' || nextDueDate === '' || nextDueDate > performedOn;
  const fileValid = file === null || file.size <= MAINTENANCE_DOCUMENT_MAX_BYTES;

  const valid =
    type !== '' &&
    activity.trim() !== '' &&
    performedOn !== '' &&
    (!renews || nextDueDate !== '') &&
    orderValid &&
    fileValid;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);
    setPercent(0);

    let recordId: string;

    try {
      const record = await maintenanceItemsApi.record(item.id, {
        type: Number(type) as MaintenanceActivityType,
        activity: activity.trim(),
        performedOn,
        performedByPersonnelId: performedBy || null,
        // Sent ONLY when the reader asked for a renewal — its presence is what moves the date.
        nextDueDate: renews ? nextDueDate : null,
        remarks: remarks.trim() || null,
      });

      recordId = record.id;
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not record the activity.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'maintenance-record' });
      setSaving(false);
      return;
    }

    if (file !== null) {
      try {
        await maintenanceItemsApi.attach(item.id, recordId, file, setPercent);
      } catch (caught) {
        toast.failure(
          `The activity was recorded, but the document was not filed: ${
            caught instanceof ApiError ? caught.message : 'the upload failed'
          } File it from the Activities tab.`,
        );
        setSaving(false);
        onSaved();
        return;
      }
    }

    toast.success(
      renews
        ? `Recorded. ${item.referenceNumber} is next due ${formatDateOnly(nextDueDate)}.`
        : `Recorded against ${item.referenceNumber}.`,
    );
    setSaving(false);
    onSaved();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Record activity on ${item.referenceNumber}`}
      size="lg"
      id="maintenance-record-modal"
      /*
        Autogenerate fills every field it can write HONESTLY — and not the document, which is a
        binary the server verifies by magic bytes. The renewal box stays at its default; the
        date behind it is filled for whoever ticks it.
      */
      autogenerate={{
        onGenerate: () => {
          const sample = sampleMaintenanceRecord(
            item.suggestedNextDueDate,
            staff.map((s) => s.id),
          );

          setType(sample.type === null ? '' : String(sample.type));
          setActivity(sample.activity);
          setPerformedOn(sample.performedOn ?? '');
          setNextDueDate(sample.nextDueDate ?? '');
          setPerformedBy(sample.performedByPersonnelId ?? '');
          setRemarks(sample.remarks ?? '');
        },
        disabled: saving,
      }}
      confirm={{
        id: 'maintenance-record-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="maintenance-record-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="maintenance-record-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            // REAL upload progress, the one exception to "Submitting" — a measurement the
            // product actually has is not flattened into a word.
            loadingLabel={percent > 0 ? `Filing ${percent}%` : 'Submitting'}
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            id="maintenance-record-type"
            label="Type"
            required
            description="Maintenance is work on the building; Compliance is a permit, certificate or inspection; Submission is a report or document filed with an office."
            placeholder="Choose a type"
            value={type}
            onChange={setType}
            sort="none"
            options={Object.entries(ACTIVITY_TYPE_LABEL).map(([value, label]) => ({ value, label }))}
            disabled={saving}
            error={error?.fieldError('type')}
          />

          <DateField
            id="maintenance-record-performed-on"
            label="Performed on"
            required
            description="The day the activity was performed."
            value={performedOn}
            onChange={setPerformedOn}
            disabled={saving}
            error={error?.fieldError('performedOn')}
          />
        </div>

        {/* FULL WIDTH — what was done is a short sentence, not a value. */}
        <TextField
          id="maintenance-record-activity"
          label="Activity"
          required
          description="What was done, e.g. Renewed the business permit or Submitted the generator log."
          value={activity}
          onChange={(e) => setActivity(e.target.value)}
          maxLength={ACTIVITY_MAX_LENGTH}
          disabled={saving}
          error={error?.fieldError('activity')}
        />

        {canReadPeople ? (
          <Select
            id="maintenance-record-performed-by-personnel-id"
            label="Performed by"
            description="The employee who performed it or saw it done."
            placeholder="Choose an employee"
            value={performedBy}
            onChange={setPerformedBy}
            options={staff.map((s) => ({ value: s.id, label: s.name }))}
            remote
            loading={loadingStaff}
            disabled={saving}
            error={error?.fieldError('performedByPersonnelId')}
          />
        ) : null}

        <Textarea
          id="maintenance-record-remarks"
          label="Remarks"
          description="Anything found, or worth knowing next time."
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          maxLength={2000}
          disabled={saving}
          error={error?.fieldError('remarks')}
        />

        <MaintenanceDocumentInput
          id="maintenance-record-document"
          file={file}
          onChange={setFile}
          disabled={saving}
        />

        {/*
          THE RENEWAL, opt-in. Unticked, the item's schedule is left alone; ticked, the next due
          date appears beneath it and the item moves to it on Submit.
        */}
        <label htmlFor="maintenance-record-renews" className="flex items-start gap-2 text-sm text-slate-600">
          <Checkbox
            id="maintenance-record-renews"
            checked={renews}
            onChange={() => setRenews((v) => !v)}
            disabled={saving}
          />
          <span>
            This renews the schedule
            <span className="block text-xs text-slate-500">
              Moves the item&apos;s due date from {formatDateOnly(item.dueDate)} to the next due date.
            </span>
          </span>
        </label>

        {renews ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <DateField
              id="maintenance-record-next-due-date"
              label="Next due date"
              required
              description={`Suggested one ${item.recurrenceLabel.toLowerCase()} period after the current due date, ${formatDateOnly(item.dueDate)}. For a renewal, type the new expiry.`}
              value={nextDueDate}
              onChange={setNextDueDate}
              disabled={saving}
              error={
                orderValid
                  ? error?.fieldError('nextDueDate')
                  : 'The next due date must come after the day it was performed.'
              }
            />
          </div>
        ) : null}

        {/* The consequence, before the tick. */}
        <p className="text-xs text-slate-600">
          {renews ? (
            <>
              Submitting moves this item&apos;s due date from {formatDateOnly(item.dueDate)} to{' '}
              {nextDueDate ? formatDateOnly(nextDueDate) : 'the date above'}.
            </>
          ) : (
            <>Submitting logs the activity. The due date stays {formatDateOnly(item.dueDate)}.</>
          )}
        </p>
      </div>
    </Modal>
  );
}
